import type { AiAgentSettingsData } from "../types";
import "./ai-agent-authority.css";

type AgentMode = AiAgentSettingsData["mode"];

function modeName(mode: AgentMode): string {
  if (mode === "paused") return "Pausado";
  if (mode === "approval_required") return "Con aprobación humana";
  return "Automático seguro";
}

export interface AiAgentAuthoritySummary {
  savedMode: string;
  selectedMode: string;
  effectiveLabel: string;
  effectiveExplanation: string;
}

/**
 * Presents known server configuration, not an inferred live runtime/provider state.
 * Only a future, separately certified server capability may report "operativo".
 */
export function agentAuthoritySummary(
  savedSettings: AiAgentSettingsData,
  selectedMode: AgentMode,
): AiAgentAuthoritySummary {
  let effectiveLabel: string;
  let effectiveExplanation: string;
  if (savedSettings.mode === "paused") {
    effectiveLabel = "No autorizada por el modo guardado";
    effectiveExplanation = "El modo guardado es Pausado. Esta pantalla no verifica el estado de los procesos externos.";
  } else if (savedSettings.mode === "approval_required") {
    effectiveLabel = "Requiere una persona";
    effectiveExplanation = "El modo guardado exige confirmación humana antes de publicar. No autoriza publicación automática.";
  } else if (savedSettings.safe_auto_consent_active !== true) {
    effectiveLabel = "Autorización no confirmada";
    effectiveExplanation = "El modo guardado es Automático seguro, pero no consta un consentimiento automático vigente en estos datos.";
  } else {
    effectiveLabel = "Ejecución efectiva sin verificar";
    effectiveExplanation = "El consentimiento figura registrado, pero no confirma que el servicio, proveedor Google ni los controles de publicación estén operativos. No se debe asumir que responde solo.";
  }
  return {
    savedMode: modeName(savedSettings.mode),
    selectedMode: modeName(selectedMode),
    effectiveLabel,
    effectiveExplanation,
  };
}

/** Always show the distinction, including while the Laboratory is open. */
export function AiAgentAuthorityPanel({
  savedSettings, selectedMode, hasUnsavedSettings,
}: {
  savedSettings: AiAgentSettingsData;
  selectedMode: AgentMode;
  hasUnsavedSettings: boolean;
}) {
  const truth = agentAuthoritySummary(savedSettings, selectedMode);
  return (
    <section className="rep-agent-authority" aria-labelledby="rep-agent-authority-title">
      <div className="rep-agent-authority-heading">
        <h2 id="rep-agent-authority-title">Estado de configuración y autorización</h2>
        <p>Elegir un modo, guardarlo y tener permiso efectivo para publicar son cosas distintas.</p>
      </div>
      <div className="rep-agent-authority-grid">
        <div>
          <span>Modo seleccionado</span>
          <strong>{truth.selectedMode}</strong>
          <p>{hasUnsavedSettings
            ? "Hay cambios locales pendientes; todavía no modifican el modo guardado."
            : "Coincide con el modo de configuración guardado."}</p>
        </div>
        <div>
          <span>Modo guardado en servidor</span>
          <strong>{truth.savedMode}</strong>
          <p>Último estado recibido desde Review Boost al abrir esta pantalla.</p>
        </div>
        <div>
          <span>Publicación automática</span>
          <strong>{truth.effectiveLabel}</strong>
          <p>{truth.effectiveExplanation}</p>
        </div>
      </div>
    </section>
  );
}
